/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import XngMiniCart from "@monsma/App/Components/XngMiniCart/XngMiniCart";
import { headerSignInStyles } from "@monsma/Overrides/Widgets/Common/HeaderSignIn";
import XPopup from "@monsma/XDL/Components/XPopup";
import { screen } from "@monsma/XDL/Helpers";
import SortArrow from "@monsma/XDL/Icons/SortArrow";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: ApplicationState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
});

const mapDispatchToProps = {
    loadCurrentCart,
};
// xng-change: adding new link styles attributes
export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    // custom
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleIconUpDown?: IconPresentationProps;
    titleText?: TypographyPresentationProps;
    cartIconCountWrapper?: InjectableCss;
    cartCountStyles?: InjectableCss;
    miniCartWrapper?: InjectableCss;
}

// xng-change: adding new Navs styles related
export const cartLinkStyles: CartLinkStyles = {
    titleClickable: {
        css: css`
            ${headerSignInStyles?.titleClickable?.css};
            ${screen("gt", "md")} {
                &.active,
                &:hover {
                    background-position: 0 100%;
                    color: ${getColor("primary.contrast")};
                    fill: ${getColor("primary.contrast")};
                    &&& * {
                        color: white;
                        fill: white;
                    }

                    .cartCount {
                        background: ${getColor("common.background")};

                        ${TypographyStyle} {
                            color: ${getColor("common.ui4")} !important;
                        }
                    }
                }
            }
        `,
    },
    titleIcon: headerSignInStyles.titleIcon,
    titleIconUpDown: headerSignInStyles.titleIconUpDown,
    titleText: headerSignInStyles.titleText,
    cartIconCountWrapper: {
        css: css`
            position: relative;

            .showCartIcon {
                visibility: visible;
            }

            .hideCartIcon {
                visibility: hidden;

                ${screen("lte", "md")} {
                    visibility: visible;
                }
            }
        `,
    },
    cartCountStyles: {
        css: css`
            position: absolute;
            left: -2px;
            top: 0;
            background: ${getColor("primary.main")};
            min-width: 18px;
            min-height: 18px;
            border-radius: 50%;
            color: ${getColor("primary.contrast")};
            text-align: center;
            padding: 0;

            & span {
                font-size: 9px;
                line-height: 18px;
                display: block;
            }

            ${screen("lte", "md")} {
                left: 9px;
                top: -9px;
            }
        `,
    },
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

const iconMap = {
    cart: ShoppingCart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

interface CartCountProps {
    lineCount: number | undefined;
    icon: any;
}

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    history,
}) => {
    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    if (isCompactHeaderCart) {
        return (
            <Link
                {...routerLink}
                color={theme.header.linkColor}
                href={cartUrl}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
            >
                <Typography as="span" {...styles.compactCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        );
    }
    // xng-change: adding CartCount circle with counter
    const CartCount = ({ lineCount, icon }: CartCountProps) => {
        return (
            <StyledWrapper {...styles.cartIconCountWrapper}>
                {Boolean(lineCount !== undefined && lineCount > 0) && (
                    <StyledWrapper
                        data-test-selector="cartLinkQuantity"
                        {...styles.cartCountStyles}
                        className="cartCount"
                    >
                        <Typography as="span">{lineCount || 0}</Typography>
                    </StyledWrapper>
                )}

                <Icon {...styles.titleIcon} src={icon} className={!lineCount ? "showCartIcon" : "hideCartIcon"} />
            </StyledWrapper>
        );
    };

    const titleClickable = (title: string, dataTestSelector: string, clickHandler: any) => {
        return (
            <Clickable {...styles.titleClickable} onClick={clickHandler} data-test-selector={dataTestSelector}>
                {showIcon && <CartCount lineCount={retainedTotalCountToDisplay} icon={icon} />}
                {showLabel && <Typography {...styles.titleText}>{translate(`${title}`)}</Typography>}
                {showIcon && <Icon {...styles.titleIconUpDown} src={SortArrow} />}
            </Clickable>
        );
    };

    const popoverTrigger = <>{titleClickable("My Cart", "cartLink", () => history.push(cartUrl as string))}</>;

    return (
        <XPopup trigger={popoverTrigger} shade={true}>
            <XngMiniCart />
        </XPopup>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(withHistory(CartLink))),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
